import { useEffect, useState } from 'react'
import { api, ApiError } from '../api'
import { useI18n, type MessageKey, type Vars } from '../i18n'
import type { AudioTrack, Release, ReleaseDetails, ReleaseTracks, SubtitleTrack } from '../protocol'
import { Icon } from '../ui/Icon'

type T = (key: MessageKey, vars?: Vars) => string

/** How many releases at the top of the list load their tracks right away. */
export const PREFETCH = 10

// Loading ---------------------------------------------------------------

class Cancelled extends Error {}

/** Runs at most max tasks at a time, skipping those nobody waits for anymore. */
class Queue {
  private running = 0
  private waiting: (() => void)[] = []

  constructor(private readonly max: number) {}

  run<V>(task: () => Promise<V>, wanted: () => boolean): Promise<V> {
    return new Promise<V>((resolve, reject) => {
      this.waiting.push(() => {
        if (!wanted()) {
          reject(new Cancelled())
          return
        }
        this.running++
        task()
          .then(resolve, reject)
          .finally(() => {
            this.running--
            this.next()
          })
      })
      this.next()
    })
  }

  private next() {
    while (this.running < this.max && this.waiting.length) this.waiting.shift()!()
  }
}

// Browsers keep about six connections per server; long file probes must
// leave room for posters and other requests.
const detailsQueue = new Queue(2)
const tracksQueue = new Queue(3)

interface Info {
  details?: ReleaseDetails
  file?: ReleaseTracks
  fileError?: { timeout: boolean; message: string }
}

// Results outlive the components: the server keeps reading a file anyway,
// and reopening a title shows what is known at once.
const known = new Map<string, Info>()
const loading = new Map<string, Promise<void>[]>()
const watchers = new Map<string, number>()
const listeners = new Map<string, Set<() => void>>()

// A season pack is probed at the first episode of the season being looked at.
const keyOf = (r: Release, season: number) => `${r.provider}/${r.id}/${season}`

function update(key: string, patch: Info) {
  known.set(key, { ...known.get(key), ...patch })
  listeners.get(key)?.forEach((l) => l())
}

function load(r: Release, season: number, token?: string) {
  const key = keyOf(r, season)
  if (loading.has(key)) return
  const wanted = () => (watchers.get(key) ?? 0) > 0
  const retry = (e: unknown) => {
    loading.delete(key) // the next watcher tries again
    return e instanceof Cancelled
  }
  loading.set(key, [
    detailsQueue
      .run(() => api.releaseDetails(r.provider, r.id, token), wanted)
      .then((details) => update(key, { details }))
      .catch((e) => {
        if (!retry(e)) update(key, { details: {} })
      }),
    tracksQueue
      .run(() => api.releaseTracks(r.provider, r.id, season, token), wanted)
      .then((file) => update(key, { file }))
      .catch((e) => {
        if (retry(e)) return
        update(key, { fileError: { timeout: e instanceof ApiError && e.code === 'timeout', message: e instanceof Error ? e.message : String(e) } })
      }),
  ])
}

/** Track information of a release; loading starts while active is true. */
function useReleaseInfo(r: Release, season: number, token: string | undefined, active: boolean): Info {
  const key = keyOf(r, season)
  const [info, setInfo] = useState<Info>(() => known.get(key) ?? {})
  useEffect(() => {
    setInfo(known.get(key) ?? {})
    const listener = () => setInfo(known.get(key) ?? {})
    let set = listeners.get(key)
    if (!set) listeners.set(key, (set = new Set()))
    set.add(listener)
    return () => {
      set.delete(listener)
    }
  }, [key])
  useEffect(() => {
    if (!active) return
    watchers.set(key, (watchers.get(key) ?? 0) + 1)
    const info = known.get(key)
    if (!info?.file && !info?.fileError) load(r, season, token)
    return () => {
      watchers.set(key, (watchers.get(key) ?? 1) - 1)
    }
    // r is identified by key.
  }, [key, token, active])
  return info
}

/** Whether anything is known about a release, so its strip can show it. */
export function hasTrackInfo(r: Release, season: number): boolean {
  return known.has(keyOf(r, season))
}

// Presentation ----------------------------------------------------------

interface Chip {
  lang?: string
  text: string
  title: string
}

const AUDIO_CODECS: Record<string, string> = { ac3: 'AC3', eac3: 'E-AC3', dts: 'DTS', truehd: 'TrueHD', aac: 'AAC', flac: 'FLAC', opus: 'Opus', mp3: 'MP3' }
const LAYOUTS: Record<number, string> = { 1: '1.0', 2: '2.0', 6: '5.1', 8: '7.1' }
const VIDEO_CODECS: Record<string, string> = { h264: 'H.264', hevc: 'HEVC', av1: 'AV1', vp9: 'VP9', mpeg4: 'MPEG-4' }

const langTag = (lang?: string) => (lang && lang !== 'und' ? lang.slice(0, 3).toUpperCase() : undefined)

/** Drops a trailing "(RUS)" that repeats the language tag. */
function shortName(name: string, lang?: string): string {
  const tag = langTag(lang)
  return tag ? name.replace(new RegExp(`\\s*\\(${tag}\\)$`, 'i'), '').trim() : name.trim()
}

function audioFormat(a: AudioTrack): string {
  return [AUDIO_CODECS[a.codec] ?? a.codec.toUpperCase(), LAYOUTS[a.channels] ?? (a.channels ? `${a.channels}ch` : '')].filter(Boolean).join(' ')
}

function subtitleFormat(s: SubtitleTrack, t: T): string {
  const parts: string[] = []
  if (s.format === 'ass') parts.push('ASS')
  if (s.forced) parts.push(t('catalog.tracks.forced'))
  if (s.format === 'pgs' || s.format === 'unsupported') parts.push(t('catalog.tracks.unsupported'))
  return parts.join(', ')
}

/** Splits "русские (forced, full), английские" at commas outside brackets. */
function splitList(s: string): string[] {
  const out: string[] = []
  let depth = 0
  let cur = ''
  for (const ch of s) {
    if ('([{'.includes(ch)) depth++
    if (')]}'.includes(ch)) depth = Math.max(0, depth - 1)
    if ((ch === ',' || ch === ';') && depth === 0) {
      out.push(cur)
      cur = ''
    } else cur += ch
  }
  out.push(cur)
  // A bare "(PGS, ASS)" qualifies the item before it.
  const items: string[] = []
  for (const x of out.map((x) => x.trim()).filter(Boolean)) {
    if (x.startsWith('(') && items.length) items[items.length - 1] += ' ' + x
    else items.push(x)
  }
  return items
}

/** The voice-over named at the end of "48 kHz, AC3, 5.1 | DUB" or "AC3 - Русский". */
function describedAudioLabel(line: string): string {
  const bar = line.split('|').map((x) => x.trim()).filter(Boolean)
  if (bar.length > 1) return bar[bar.length - 1]
  const dash = line.split(' - ')
  return dash.length > 1 ? dash[dash.length - 1].trim() : line
}

function chips(info: Info, t: T): { audio: Chip[]; subtitles: Chip[]; source: 'file' | 'description' } | null {
  if (info.file) {
    return {
      source: 'file',
      audio: info.file.audio.map((a) => {
        const text = shortName(a.name, a.language) || langTag(a.language) || '?'
        return { lang: langTag(a.language), text, title: [a.name, audioFormat(a), a.external ? t('catalog.tracks.external') : ''].filter(Boolean).join(' · ') }
      }),
      subtitles: info.file.subtitles.map((s) => {
        const text = shortName(s.name, s.language) || (s.forced ? t('catalog.tracks.forced') : '')
        return { lang: langTag(s.language), text, title: [s.name, subtitleFormat(s, t)].filter(Boolean).join(' · ') }
      }),
    }
  }
  const d = info.details
  if (!d || !(d.translation?.length || d.audio?.length || d.subtitles?.length)) return null
  const voices = d.translation?.length ? d.translation.flatMap(splitList) : (d.audio ?? []).map(describedAudioLabel)
  return {
    source: 'description',
    audio: [...new Set(voices)].map((v) => ({ text: v, title: v })),
    subtitles: (d.subtitles ?? []).flatMap(splitList).map((s) => ({ text: s, title: s })),
  }
}

function ChipList({ items, max }: { items: Chip[]; max: number }) {
  const shown = items.slice(0, max)
  const rest = items.length - shown.length
  return (
    <>
      {shown.map((c, i) => (
        <span key={i} className="tchip" title={c.title}>
          {c.lang && <span className="tchip__lang">{c.lang}</span>}
          {c.text && c.text !== c.lang && <span className="tchip__text">{c.text}</span>}
        </span>
      ))}
      {rest > 0 && (
        <span className="tchip tchip--more" title={items.slice(max).map((c) => c.title).join('\n')}>
          +{rest}
        </span>
      )}
    </>
  )
}

/**
 * A one-line summary of a release's tracks under its title, with a toggle
 * for the full list. Releases at the top of the list load it at once.
 */
export function ReleaseTracksStrip({ release, season, token, prefetch, open, onToggle }: { release: Release; season: number; token?: string; prefetch: boolean; open: boolean; onToggle: () => void }) {
  const { t } = useI18n()
  const info = useReleaseInfo(release, season, token, prefetch || open)
  const c = chips(info, t)
  const probing = !info.file && !info.fileError
  const started = prefetch || open || hasTrackInfo(release, season)

  return (
    <div className="rstrip">
      {c ? (
        <>
          {c.source === 'file' ? (
            <span className="rstrip__source rstrip__source--file" title={t('catalog.tracks.fileHint')}>
              <Icon name="check" /> {t('catalog.tracks.fileShort')}
            </span>
          ) : (
            <span className="rstrip__source" title={probing ? t('catalog.tracks.descriptionHint') : undefined}>
              {probing && <span className="releases__spinner rstrip__spinner" aria-hidden="true" />} {t('catalog.tracks.descriptionShort')}
            </span>
          )}
          {info.file && dolbyVisionOnly(info.file) && (
            <span className="rstrip__source rstrip__source--warn" title={t('hdr.dvOnly')}>
              Dolby Vision 5
            </span>
          )}
          {/* A description that does not mention a kind of track says nothing about it. */}
          {(c.audio.length > 0 || c.source === 'file') && (
            <span className="rstrip__group" aria-label={t('catalog.tracks.audio')}>
              <span className="rstrip__icon" title={t('catalog.tracks.audio')}>
                <Icon name="volume" />
              </span>
              {c.audio.length ? <ChipList items={c.audio} max={4} /> : <span className="muted">{t('catalog.tracks.none')}</span>}
            </span>
          )}
          {(c.subtitles.length > 0 || c.source === 'file') && (
            <span className="rstrip__group" aria-label={t('catalog.tracks.subtitles')}>
              <span className="rstrip__icon" title={t('catalog.tracks.subtitles')}>
                <Icon name="subtitles" />
              </span>
              {c.subtitles.length ? <ChipList items={c.subtitles} max={3} /> : <span className="muted">{t('catalog.tracks.none')}</span>}
            </span>
          )}
        </>
      ) : started && (probing || !info.details) ? (
        <span className="rstrip__skeleton" aria-label={t('catalog.tracks.loading')}>
          <span className="tchip tchip--skeleton" />
          <span className="tchip tchip--skeleton" />
          <span className="tchip tchip--skeleton" />
        </span>
      ) : started ? (
        <span className="muted">{t('catalog.tracks.unknown')}</span>
      ) : null}
      <button type="button" className="rstrip__toggle" aria-expanded={open} onClick={onToggle}>
        {open ? t('catalog.tracks.less') : started ? t('catalog.tracks.more') : t('catalog.tracks.button')}
        <span className={`rstrip__caret${open ? ' is-open' : ''}`} aria-hidden="true">
          ▾
        </span>
      </button>
    </div>
  )
}

/** The full track list of a release. */
export function ReleaseTracksPanel({ release, season, token }: { release: Release; season: number; token?: string }) {
  const { t } = useI18n()
  const info = useReleaseInfo(release, season, token, true)
  const { file, details, fileError } = info

  if (file) {
    const video = file.video.width ? `${file.video.width}×${file.video.height} ${VIDEO_CODECS[file.video.codec] ?? file.video.codec}` : ''
    return (
      <div className="rtracks">
        <header className="rtracks__head">
          <span className="rtracks__badge rtracks__badge--file">
            <Icon name="check" /> {t('catalog.tracks.fromFile')}
          </span>
          <span className="rtracks__file" title={file.file}>
            {file.file.slice(file.file.lastIndexOf('/') + 1)}
          </span>
          {video && <span className="rtracks__fact">{video}</span>}
          {rangeText(file) && <span className="rtracks__fact rtracks__fact--hdr">{rangeText(file)}</span>}
          {file.videos > 1 && <span className="rtracks__fact">{t('catalog.tracks.episode', { n: file.videos })}</span>}
        </header>
        {dolbyVisionOnly(file) && <p className="rtracks__warn">{t('hdr.dvOnly')}</p>}
        <Group icon="volume" title={t('catalog.tracks.audio')} count={file.audio.length}>
          {file.audio.map((a, i) => (
            <Row key={i} lang={langTag(a.language)} name={shortName(a.name, a.language)} extra={[audioFormat(a), a.external ? t('catalog.tracks.external') : ''].filter(Boolean).join(' · ')} />
          ))}
        </Group>
        <Group icon="subtitles" title={t('catalog.tracks.subtitles')} count={file.subtitles.length}>
          {file.subtitles.map((s, i) => (
            <Row key={i} lang={langTag(s.language)} name={shortName(s.name, s.language)} extra={[subtitleFormat(s, t), s.external ? t('catalog.tracks.external') : ''].filter(Boolean).join(' · ')} />
          ))}
        </Group>
      </div>
    )
  }

  const described = details && (details.translation?.length || details.audio?.length || details.subtitles?.length)
  return (
    <div className="rtracks">
      <header className="rtracks__head">
        {described ? <span className="rtracks__badge">{t('catalog.tracks.fromDescription')}</span> : null}
        {fileError ? (
          <span className="rtracks__status muted">{t('catalog.tracks.probeFailed', { error: fileError.timeout ? t('catalog.tracks.timeout') : fileError.message })}</span>
        ) : (
          <span className="rtracks__status muted">
            <span className="releases__spinner" aria-hidden="true" /> {t('catalog.tracks.probing')}
          </span>
        )}
      </header>
      {described ? (
        <>
          {details.translation?.length ? (
            <Group icon="voice" title={t('catalog.tracks.translation')}>
              {details.translation.map((x, i) => (
                <Row key={i} name={x} />
              ))}
            </Group>
          ) : null}
          {details.audio?.length ? (
            <Group icon="volume" title={t('catalog.tracks.audio')} count={details.audio.length}>
              {details.audio.map((x, i) => (
                <Row key={i} name={x} />
              ))}
            </Group>
          ) : null}
          {details.subtitles?.length ? (
            <Group icon="subtitles" title={t('catalog.tracks.subtitles')}>
              {details.subtitles.map((x, i) => (
                <Row key={i} name={x} />
              ))}
            </Group>
          ) : null}
        </>
      ) : (
        details && <p className="rtracks__empty muted">{t('catalog.tracks.notInDescription')}</p>
      )}
    </div>
  )
}

function Group({ icon, title, count, children }: { icon: string; title: string; count?: number; children: React.ReactNode[] }) {
  const { t } = useI18n()
  return (
    <section className="rtracks__group">
      <h4 className="rtracks__title">
        <Icon name={icon} /> {title}
        {count ? <span className="rtracks__count">{count}</span> : null}
      </h4>
      {children.length ? <ul className="rtracks__rows">{children}</ul> : <p className="rtracks__empty muted">{t('catalog.tracks.none')}</p>}
    </section>
  )
}

function Row({ lang, name, extra }: { lang?: string; name: string; extra?: string }) {
  return (
    <li className="rtracks__row">
      {lang && <span className="tchip__lang">{lang}</span>}
      <span className="rtracks__name">{name || lang || '—'}</span>
      {extra && <span className="rtracks__extra">{extra}</span>}
    </li>
  )
}

/** "HDR10", "HLG" or "Dolby Vision 8.1" from the file, empty for SDR. */
function rangeText(f: ReleaseTracks): string {
  const v = f.video
  if (v.dolbyVision) return `Dolby Vision ${v.dolbyVision}${v.dvCompat ? `.${v.dvCompat}` : ''}`
  if (v.range === 'hlg') return 'HLG'
  return v.range === 'pq' ? 'HDR10' : ''
}

const dolbyVisionOnly = (f: ReleaseTracks) => !!f.video.dolbyVision && !f.video.dvCompat
